/**
 * App Router
 *
 * Constitution Section 19.5: React 19 Usage Expectations
 * Route-Level Code Splitting with React.lazy and Suspense
 */

import { Suspense, useCallback } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useNavigate,
  type FutureConfig,
} from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import TokenConfigProvider from '@/components/TokenConfigProvider';
import CompanyLoader from '@/components/CompanyLoader';
import ProtectedRoute from '@/components/ProtectedRoute';
import AuthEventsTracker from '@/components/AuthEventsTracker';
import { markAuthCallbackPending } from '@/lib/analytics/authEvents';
import MainLayout from '@/components/layout/MainLayout';
import { PageSkeleton, CompactPageSkeleton, ChatPageSkeleton } from '@/components/common/PageSkeleton';

// Lazy-loaded route components
import * as LazyRoutes from './lazy-routes';

// Feature-scoped route arrays (card lRqCSV1P — M3.4 pilots router consolidation).
import { pilotRoutes } from '@/features/pilots/routes';

const futureConfig = { v7_startTransition: true } as Partial<FutureConfig>;

/**
 * Card k33zTn1p (2026-07-12) — bridge key used by ``Auth0Layout``'s
 * ``onRedirectCallback`` and ``Auth0Callback``'s fallback effect to share
 * the ``appState.returnTo`` value across the two render paths. Primary
 * navigation happens inside ``onRedirectCallback`` (which has access to
 * ``useNavigate`` because ``Auth0Provider`` now lives inside the router
 * context). The sessionStorage write is defense-in-depth: if
 * ``Auth0Callback`` renders its authenticated branch before the router
 * commits the navigate, it reads the same target instead of hardcoding
 * ``/dashboard``.
 */
export const AUTH_RETURN_TO_STORAGE_KEY = 'inbest:auth:returnTo';

/**
 * Card k33zTn1p (2026-07-12) — layout route that hosts ``Auth0Provider``
 * INSIDE the router so ``onRedirectCallback`` can call ``useNavigate`` to
 * honour ``appState.returnTo``. Previously ``Auth0Provider`` wrapped the
 * router at the ``AppRouter`` level and had no ``onRedirectCallback`` at
 * all — so the SDK's default reset put every post-login user at the base
 * URL and ``Auth0Callback`` had to hard-code ``/dashboard``, breaking the
 * wizard → login → chat flow (the user lost the ``conversationId`` /
 * typed-block context saved on ``/investment/chat``).
 *
 * The pathless layout matches every URL, so ``Auth0Provider`` +
 * ``TokenConfigProvider`` + ``CompanyLoader`` mount exactly once — same as
 * before — but ``onRedirectCallback`` now has access to ``navigate`` and
 * routes users back to their original page.
 */
function Auth0Layout() {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH0_CALLBACK_URL;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  const onRedirectCallback = useCallback(
    (appState?: { returnTo?: string } | null) => {
      const target =
        appState &&
        typeof appState.returnTo === 'string' &&
        appState.returnTo.length > 0
          ? appState.returnTo
          : '/dashboard';
      // Persist for the ``Auth0Callback`` fallback path. In the happy path
      // the ``navigate`` below fires FIRST and the callback component
      // unmounts before its effect body runs, so this write is dead-code
      // in practice. Kept as defense-in-depth against SDK / React timing
      // corner cases where the effect races the navigate.
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(AUTH_RETURN_TO_STORAGE_KEY, target);
        }
      } catch {
        // Private mode / SecurityError — the navigate() below still fires.
      }
      // Card z838xWhL (OBS F1.2) — señaliza al ``AuthEventsTracker`` que
      // un redirect Auth0 real acaba de completar. El tracker consume
      // este flag para distinguir un redirect callback de un silent-auth
      // refresh (que también flipea ``isAuthenticated`` a ``true``).
      markAuthCallbackPending();
      navigate(target, { replace: true });
    },
    [navigate],
  );

  if (!(domain && clientId && redirectUri && audience)) {
    console.error('Auth0 configuration missing:', { domain, clientId, redirectUri, audience });
    return <div>Auth0 configuration missing</div>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: "openid profile email read:companies create:companies update:companies delete:companies read:all_companies read:projects create:projects update:projects delete:projects read:properties create:properties update:properties delete:properties offline_access"
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <TokenConfigProvider>
        {/* Card z838xWhL (OBS F1.2) — emite user_signup / user_login vía
            useTrackEvent cuando un redirect Auth0 real completa. */}
        <AuthEventsTracker />
        <CompanyLoader />
        <Outlet />
      </TokenConfigProvider>
    </Auth0Provider>
  );
}

const router = createBrowserRouter(
  [
    {
      // Card k33zTn1p — pathless layout wraps every route with ``Auth0Provider``
      // so ``onRedirectCallback`` can access ``useNavigate``. Same
      // always-mounted semantics as the previous ``AppRouter`` wrapper.
      element: <Auth0Layout />,
      children: [
    // Rutas canarias para diagnóstico
    { path: '/__ping', element: <div>__PING_OK</div> },
    { path: '/__router', element: <div>ROUTER_V2</div> },
  
    // Rutas públicas (lightweight - can be eager loaded if needed)
    {
      path: '/',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.LandingPage />
        </Suspense>
      ),
    },
    {
      path: '/login',
      element: (
        <Suspense fallback={<CompactPageSkeleton />}>
          <LazyRoutes.LoginPage />
        </Suspense>
      ),
    },
    { path: '/about', element: <Navigate to="/acerca" replace /> },
    {
      path: '/terminos',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.TerminosPage />
        </Suspense>
      ),
    },
    {
      path: '/tratamiento-datos',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.TratamientoDatosPage />
        </Suspense>
      ),
    },
    {
      path: '/acerca',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.AcercaPage />
        </Suspense>
      ),
    },
    {
      path: '/callback',
      element: (
        <Suspense fallback={<CompactPageSkeleton />}>
          <LazyRoutes.Auth0Callback />
        </Suspense>
      ),
    },
    {
      path: '/invitations/accept',
      element: (
        <Suspense fallback={<CompactPageSkeleton />}>
          <LazyRoutes.InvitationAccept />
        </Suspense>
      ),
    },
    
    // Rutas de prueba — solo disponibles en desarrollo
    ...(import.meta.env.DEV ? [
      {
        path: '/test',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <LazyRoutes.BackendTestPage />
          </Suspense>
        ),
      },
      {
        path: '/test-simple',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <LazyRoutes.TestPage />
          </Suspense>
        ),
      },
      {
        path: '/pruebas',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <LazyRoutes.SimpleTestPage />
          </Suspense>
        ),
      },
      {
        path: '/test-direct',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <LazyRoutes.TestDirectPage />
          </Suspense>
        ),
      },
      {
        path: '/auth-test',
        element: (
          <Suspense fallback={<CompactPageSkeleton />}>
            <LazyRoutes.AuthTestComponent />
          </Suspense>
        ),
      },
      {
        path: '/semantic-tuning',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <LazyRoutes.SemanticTuningPanel />
          </Suspense>
        ),
      },
    ] : []),
  
    // Chat - Ruta pública (mismo flujo autenticado o no). El chat responde 200
    // a anónimos; la captura de lead se hace vía LeadBridgeModal + wizard.
    // Canonical URL: /investment/chat
    { path: '/chat', element: <Navigate to="/investment/chat" replace /> },
    {
      path: '/investment/chat',
      element: (
        <Suspense fallback={<ChatPageSkeleton />}>
          <LazyRoutes.ChatPage />
        </Suspense>
      ),
    },

    // Card jMJzJXGJ — pantallas post-wizard (GoFest modulos 2 + 4).
    // Fuera del MainLayout: son standalone del flujo perfilador → chat, no
    // dashboard B2B. La auth la enforcerea el endpoint BE (POST
    // /api/v1/financial-profile requiere Auth0 JWT).
    {
      path: '/investment/capacidad',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.CapacidadPage />
        </Suspense>
      ),
    },
    {
      path: '/investment/financiacion',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.FinanciacionPage />
        </Suspense>
      ),
    },
    
    // Feature 053: Hybrid Search Page with UI filters
    {
      path: '/search',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.HybridSearchPage />
        </Suspense>
      ),
    },
    
    // Feature 056: Shared Comparison Page (public - no auth required)
    {
      path: '/compare/:shareCode',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.SharedComparisonPage />
        </Suspense>
      ),
    },

    // Public Project Page (no auth) — reached from chat "Ver proyecto completo"
    {
      path: '/p/:projectId',
      element: (
        <Suspense fallback={<PageSkeleton />}>
          <LazyRoutes.PublicProjectPage />
        </Suspense>
      ),
    },

    // Rutas protegidas que usan el MainLayout
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      ),
      children: [
        // Dashboard (heavy)
        {
          path: 'dashboard',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.DashboardPage />
            </Suspense>
          ),
        },

        // Market Insights Dashboard (B2B Owner-only — card 6a2dc9e6 W6)
        {
          path: 'dashboard/market-insights',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.MarketInsightsPage />
            </Suspense>
          ),
        },

        // Company Analytics Dashboard (B2B — card 76w0BlgU)
        {
          path: 'dashboard/analytics',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyAnalyticsPage />
            </Suspense>
          ),
        },

        // Proyectos (heavy - forms, data tables)
        {
          path: 'projects',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.ProjectsPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/new',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CreateProjectPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.ProjectDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId/edit',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.EditProjectPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId/add-property',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AddPropertyPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId/properties',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.ProjectPropertiesPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId/properties/:propertyId/edit',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.EditProjectPropertyPage />
            </Suspense>
          ),
        },
        
        // Modelos de Propiedad (heavy)
        {
          path: 'projects/:projectId/property-models/new',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CreatePropertyModelPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId/property-models/:modelId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.PropertyModelViewPage />
            </Suspense>
          ),
        },
        {
          path: 'projects/:projectId/property-models/:modelId/edit',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.EditPropertyModelPage />
            </Suspense>
          ),
        },
        
        // Propiedades (heavy)
        {
          path: 'properties',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.PropertiesPage />
            </Suspense>
          ),
        },
        {
          path: 'properties/new',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CreateIndependentPropertyPage />
            </Suspense>
          ),
        },
        {
          path: 'properties/:propertyId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.PropertyDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'properties/:propertyId/edit',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.EditPropertyPage />
            </Suspense>
          ),
        },
        
        // Empresa - Redirecciones de compatibilidad
        { path: 'company', element: <Navigate to="/companies" replace /> },
        { path: 'company/new', element: <Navigate to="/companies" replace /> },
        { path: 'company/:companyId/edit', element: <Navigate to="/companies" replace /> },
        {
          path: 'company/:companyId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyDetailPage />
            </Suspense>
          ),
        },
        
        // Empresas (heavy)
        {
          path: 'companies',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.Companies />
            </Suspense>
          ),
        },
        {
          path: 'companies/new',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyCreatePage />
            </Suspense>
          ),
        },
        {
          path: 'companies/:companyId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyDetail />
            </Suspense>
          ),
        },
        {
          path: 'companies/:companyId/edit',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyEditPage />
            </Suspense>
          ),
        },
        {
          path: 'companies/:companyId/members',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyMembersPage />
            </Suspense>
          ),
        },
        {
          path: 'companies/:companyId/settings',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanySettingsPage />
            </Suspense>
          ),
        },
        {
          path: 'companies/:companyId/settings/custom-fields',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.CompanyCustomFieldsSettingsPage />
            </Suspense>
          ),
        },

        // Invitations (user's pending invitations)
        {
          path: 'invitations',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.MyInvitationsPage />
            </Suspense>
          ),
        },

        // Leads (Feature 061: Smart Lead v2.0; card V57jW8x8 consolidó OWNER + MEMBER aquí)
        {
          path: 'leads',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.LeadsPage />
            </Suspense>
          ),
        },

        // Card V57jW8x8 — `/my-leads` se conserva como redirect para bookmarks y
        // URLs externas (pYObsyo4 introdujo la ruta separada; ya no existe).
        { path: 'my-leads', element: <Navigate to="/leads" replace /> },

        // Favorites (vJgEO72n: 3-level favorites)
        {
          path: 'favorites',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.FavoritesPage />
            </Suspense>
          ),
        },

        // Administración (HEAVY - admin components, analytics, etc.)
        {
          path: 'admin/companies',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminCompaniesPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/companies/:companyId/members',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminCompanyMembersPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/companies/:companyId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminCompanyDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/properties',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminPropertiesPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/properties/:propertyId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminPropertyDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/projects',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminProjectsPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/projects/:projectId',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.ProjectDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/projects/:projectId/edit',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.EditProjectPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/analytics',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AnalyticsPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/leads',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminLeadsPage />
            </Suspense>
          ),
        },
        {
          // Card uUTiurxO — admin editor cajas/subsidios (sin redeploy).
          path: 'admin/cajas',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AdminCajasPage />
            </Suspense>
          ),
        },

        // Perfil (lightweight)
        {
          path: 'profile',
          element: (
            <Suspense fallback={<CompactPageSkeleton />}>
              <LazyRoutes.ProfilePage />
            </Suspense>
          ),
        },

        // Asistente IA Studio
        {
          path: 'ai-studio',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.AIAssistantStudioPage />
            </Suspense>
          ),
        },

        // Property Wizard (conversational flow — demo/mock)
        {
          path: 'property-wizard',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <LazyRoutes.PropertyWizardPage />
            </Suspense>
          ),
        },

        // Pilots — módulo consolidado (card lRqCSV1P — M3.4).
        //
        // El registro completo de rutas ``/pilots/*`` (list, detail, F1
        // wizard + 5 placeholders M4) vive en ``features/pilots/routes``.
        // Este spread mantiene la separación router-global vs feature.
        //
        // TODO(rbac): las rutas admin (``pilots/new``, ``committee``) usan
        // ``RequireAdminPilot`` del feature. Las rutas por-piloto quedan
        // protegidas por ProtectedRoute (auth) + membership backend. Cuando
        // el rol PILOT_LEAD se mapee en el frontend session store (hoy
        // ``'investor' | 'owner' | 'developer' | 'admin'``), el gate puede
        // extenderse con RoleRoute allow={['pilot_lead','admin']}.
        ...pilotRoutes,
      ],
    },

        // Redirección por defecto
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ],
  { future: futureConfig }
);

/**
 * Card k33zTn1p (2026-07-12): ``Auth0Provider`` moved into ``Auth0Layout``
 * inside the router so ``onRedirectCallback`` can use ``useNavigate``.
 * ``AppRouter`` now only mounts ``RouterProvider``; the Auth0 config sanity
 * check runs inside ``Auth0Layout`` where the env vars are consumed.
 */
export default function AppRouter() {
  return <RouterProvider router={router} />;
}
